// Residency is the traveller's citizenship. ETG takes it as an ISO 3166-1
// alpha-2 code in lowercase and uses it to select rates a national of that
// country may actually book, so it changes prices and availability — it is not
// cosmetic. Two of RateHawk's mandatory certification cases pin it explicitly
// (Uzbekistan `uz` and Monaco `mc`).
//
import { detectUserCountry } from './currency';

// Only the codes are held here; display names come from the browser's own
// locale data via Intl, so there is no hand-maintained country table to drift.

const COUNTRY_CODES = [
    'ad', 'ae', 'af', 'ag', 'al', 'am', 'ao', 'ar', 'at', 'au', 'az',
    'ba', 'bb', 'bd', 'be', 'bf', 'bg', 'bh', 'bi', 'bj', 'bn', 'bo', 'br', 'bs', 'bt', 'bw', 'by', 'bz',
    'ca', 'cd', 'cf', 'cg', 'ch', 'ci', 'cl', 'cm', 'cn', 'co', 'cr', 'cu', 'cv', 'cy', 'cz',
    'de', 'dj', 'dk', 'dm', 'do', 'dz',
    'ec', 'ee', 'eg', 'er', 'es', 'et',
    'fi', 'fj', 'fr',
    'ga', 'gb', 'gd', 'ge', 'gh', 'gm', 'gn', 'gq', 'gr', 'gt', 'gw', 'gy',
    'hn', 'hr', 'ht', 'hu',
    'id', 'ie', 'il', 'in', 'iq', 'ir', 'is', 'it',
    'jm', 'jo', 'jp',
    'ke', 'kg', 'kh', 'ki', 'km', 'kn', 'kp', 'kr', 'kw', 'kz',
    'la', 'lb', 'lc', 'li', 'lk', 'lr', 'ls', 'lt', 'lu', 'lv', 'ly',
    'ma', 'mc', 'md', 'me', 'mg', 'mh', 'mk', 'ml', 'mm', 'mn', 'mr', 'mt', 'mu', 'mv', 'mw', 'mx', 'my', 'mz',
    'na', 'ne', 'ng', 'ni', 'nl', 'no', 'np', 'nz',
    'om',
    'pa', 'pe', 'pg', 'ph', 'pk', 'pl', 'pt', 'pw', 'py',
    'qa',
    'ro', 'rs', 'ru', 'rw',
    'sa', 'sb', 'sc', 'sd', 'se', 'sg', 'si', 'sk', 'sl', 'sm', 'sn', 'so', 'sr', 'ss', 'sv', 'sy', 'sz',
    'td', 'tg', 'th', 'tj', 'tl', 'tm', 'tn', 'to', 'tr', 'tt', 'tv', 'tw', 'tz',
    'ua', 'ug', 'us', 'uy', 'uz',
    'va', 'vc', 've', 'vn', 'vu',
    'ws',
    'ye',
    'za', 'zm', 'zw'
];

/**
 * The traveller's saved choice if they have made one, otherwise the country
 * detected from their locale/timezone. The search always carries a nationality;
 * the traveller can change it in the search bar.
 */
function getDefaultResidency(): string {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            const stored = window.localStorage.getItem('suitescanner-residency');
            if (stored && COUNTRY_CODES.includes(stored.toLowerCase())) {
                return stored.toLowerCase();
            }
        }
    } catch {}

    return normalizeResidency(detectUserCountry());
}

function setDefaultResidency(code: string): void {
    try {
        if (typeof window !== 'undefined' && window.localStorage && COUNTRY_CODES.includes(code.toLowerCase())) {
            window.localStorage.setItem('suitescanner-residency', code.toLowerCase());
        }
    } catch {}
}

function normalizeResidency(code?: string | null): string {
    const normalized = code?.toLowerCase() ?? '';
    return COUNTRY_CODES.includes(normalized) ? normalized : '';
}

// Only used to initialize the telephone dial code, never a rate search.
const DEFAULT_PHONE_COUNTRY = 'in';

type ResidencyOption = { code: string; name: string };

// Intl.DisplayNames is unavailable on some older engines; fall back to the
// upper-cased code rather than shipping a blank row.
function displayNames(): { of(code: string): string | undefined } | null {
    const intl = Intl as typeof Intl & {
        DisplayNames?: new (locales: string[], options: { type: string }) => { of(code: string): string | undefined };
    };

    if (!intl.DisplayNames) {
        return null;
    }

    try {
        return new intl.DisplayNames(['en'], { type: 'region' });
    } catch {
        return null;
    }
}

function buildOptions(): ResidencyOption[] {
    const names = displayNames();

    return COUNTRY_CODES
        .map(code => ({
            code,
            name: names?.of(code.toUpperCase()) ?? code.toUpperCase()
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

// The list never changes within a session — build it once.
const RESIDENCY_OPTIONS: ResidencyOption[] = buildOptions();

function residencyName(code: string): string {
    return RESIDENCY_OPTIONS.find(option => option.code === code)?.name ?? code.toUpperCase();
}

export { DEFAULT_PHONE_COUNTRY, getDefaultResidency, setDefaultResidency, normalizeResidency, RESIDENCY_OPTIONS, residencyName };
export type { ResidencyOption };
